import cx from "classnames";
import moment from "moment";
import React from "react";

import Button from "components/Button";
import { Card } from "components/Card";
import DatePickerPopover from "components/DatePickerPopover";
import Dropdown from "components/Dropdown";
import Table from "components/Table";
import Constants from "helpers/constants";
import { ResetNote } from "interfaces/reset-notes";
import { anyFieldsEmpty } from "utils/helpers";
import store2project from "state/store2project";

interface ResetNotesUIProps {
  isMobile: boolean;
  onSubmit: (e: React.FormEvent, data: { resetNote: string; implementation: string }) => void;
  onTakeSurveys: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDateChange: (date: moment.Moment| null, type: "actual" | "target") => void;
  onImplementationChange: (value: string) => void;
  storeStatus: string;
  resetnotes: ResetNote[];
  displaySurveys: boolean;
  store2project: any;
}

interface ResetNotesUState {
  resetNote: string;
  implementation: string;
  noteOverLength?: boolean;
  targetResetDate: moment.Moment| null;
  actualResetDate: moment.Moment| null;
  userSelectedImplementation: boolean;
}

const implementationOptions = [
  // { label: "N/A", value: "N/A", "data-qa": "N/A" },
  { label: "Yes", value: "Yes", "data-qa": "Yes" },
  { label: "No", value: "No", "data-qa": "No" },
];

const defaultState = {
  resetNote: "",
  implementation: "N/A",
  targetResetDate: moment(),
  actualResetDate: moment(),
  noteOverLength: false,
  userSelectedImplementation: false,
};

class ResetNotesUI extends React.Component<ResetNotesUIProps, ResetNotesUState> {
  private MAX_NOTE_LENGTH = 500;
  state = { ...defaultState };

  public componentDidMount() {
    // const status = this.props.storeStatus?.toUpperCase();

    // let implementation = "N/A";

    // if (status === Constants.RESET_SCHEDULED) {
    //   implementation = "No";
    // } else if (status === Constants.RESET_COMPLETE) {
    //   implementation = "Yes";
    // }

    this.setState({ implementation: this.getDefaultImplementation() });
  }

  public componentDidUpdate(prevProps: ResetNotesUIProps, prevState: ResetNotesUState) {
      //const status = this.props.storeStatus?.toUpperCase();

      // let implementation = "N/A";

      // if (status === Constants.RESET_SCHEDULED) {
      //   implementation = "No";
      // } else if (status === Constants.RESET_COMPLETE) {
      //   implementation = "Yes";
      // }
    const statusChanged = prevProps.storeStatus !== this.props.storeStatus;
    const targetDateChanged =
      prevProps.store2project?.target_reset_date !== this.props.store2project?.target_reset_date;
    const actualDateChanged =
      prevProps.store2project?.actual_reset_date !== this.props.store2project?.actual_reset_date;

    // Only auto-set if props changed (not state) AND user has NOT manually selected
    if (
      (statusChanged || targetDateChanged || actualDateChanged) &&
      !this.state.userSelectedImplementation
    ) {
      this.setState({ implementation: this.getDefaultImplementation() });
    }
  }

  public handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.currentTarget.id === "add_resetnote") {
      const value = event.currentTarget.value;
      this.setState({
        resetNote: value,
        noteOverLength: value.length > this.MAX_NOTE_LENGTH,
      });
    }
  };

  public handleTakeSurveys = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    this.props.onTakeSurveys(e);
  };

  public handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const { resetNote, implementation } = this.state;

    this.props.onSubmit(e, {
      resetNote,
      implementation,
    });
    this.setState({ ...defaultState, implementation: this.state.implementation || "N/A", userSelectedImplementation: false });
  };

  public handleDateChange = (date: moment.Moment | null, type: "actual" | "target") => {
    if (type === "actual") {
      this.setState({ actualResetDate: date });
    } else if (type === "target") {
      this.setState({ targetResetDate: date });
    }
    this.props.onDateChange(date, type);
  };

    public handleTimeChange = (date: moment.Moment | null, type: "actual" | "target") => {
  // public handleTimeChange = (date: moment.Moment | null, type: string) => {
    if (!date) {
      this.handleDateChange(null, type);
      return;
    }
    const time = date.format("h:mm a");
    let newDateTime;
    if (type === "actual") {
      newDateTime = moment(
        `${this.state.actualResetDate.format("MM/D/YYYY")} ${time}`,
        "MM/DD/YYYY h:mm a"
      );
      this.setState({ actualResetDate: date });
      return newDateTime && this.props.onDateChange(newDateTime, "actual");
    } else if (type === "target") {
      newDateTime = moment(
        `${this.state.targetResetDate.format("MM/D/YYYY")} ${time}`,
        "MM/DD/YYYY h:mm a"
      );
      this.setState({ targetResetDate: date });
      return newDateTime && this.props.onDateChange(newDateTime, "target");
    }
  };

  private getDefaultImplementation = (): string => {
    // Never override if user already picked something
    if (this.state?.userSelectedImplementation) {
      return this.state.implementation;
    }

    const { storeStatus, store2project } = this.props;
    const status = storeStatus?.toUpperCase();

    if (store2project?.target_reset_date || store2project?.actual_reset_date) {
      return "N/A";
    }

    if (status === Constants.RESET_SCHEDULED) return "No";
    if (status === Constants.RESET_COMPLETE) return "Yes";

    return "N/A";
  };

  public render() {
    const { resetNote } = this.state;
    const { isMobile } = this.props;
    const canEditResetDate =
      this.props.storeStatus?.toUpperCase() === Constants.VALIDATION_COMPLETE ||
      this.props.storeStatus?.toUpperCase() === Constants.FILES_AVAILABLE ||
      this.props.storeStatus?.toUpperCase() === Constants.RESET_SCHEDULED ||
      this.props.storeStatus?.toUpperCase() === Constants.RESET_COMPLETE;

    const canEditImplementation =
      this.props.storeStatus?.toUpperCase() === Constants.VALIDATION_COMPLETE ||
      this.props.storeStatus?.toUpperCase() === Constants.RESET_SCHEDULED ||
      this.props.storeStatus?.toUpperCase() === Constants.RESET_COMPLETE;


    const submitDisabled = anyFieldsEmpty({ note: this.state.resetNote });
    return (
      <div>
        <div style={{ borderRadius: "16px", border: "1px solid #e3e6ea", overflow: "hidden" }}>
          <div
            style={{
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <h3 style={{ marginRight: 20, fontSize: "16px" }} data-qa="reset-notes-header">
              Add Reset Notes
            </h3>
          </div>
          <Card.Body>
            <div className="date-form-group">
              <div className="date-label-group" data-qa="target-reset-date">
                <h6 style={{ fontSize: "12px" }}>Target Reset Date</h6>
                <DatePickerPopover
                  fieldValue={this.props.store2project.target_reset_date}
                  field={true}
                  defaultValue={""}
                  dateFormat={isMobile ? "MM/D/YYYY" : "MM/D/YYYY h:mm a"}
                  isMobile={isMobile}
                  disabled={!canEditResetDate}
                  onSave={(date: moment.Moment | null) => {
                    this.handleDateChange(date, "target");
                  }}
                />
              </div>
              {isMobile && (
                <div className="date-label-group" data-qa="target-reset-date">
                  <h6>Target Reset Time</h6>
                  <DatePickerPopover
                    fieldValue={this.props.store2project.target_reset_date}
                    field={true}
                    defaultValue={""}
                    isMobile={isMobile}
                    disabled={!canEditResetDate}
                    timeOnly={true}
                    onSave={(date: moment.Moment | null) => {
                      this.handleTimeChange(date, "target");
                    }}
                  />
                </div>
              )}
              <div className="date-label-group" data-qa="actual-reset-date">
                <h6>Actual Reset Date</h6>
                <DatePickerPopover
                  field={true}
                  fieldValue={this.props.store2project.actual_reset_date}
                  defaultValue={""}
                  dateFormat={isMobile ? "MM/D/YYYY" : "MM/D/YYYY h:mm a"}
                  isMobile={isMobile}
                  disabled={!canEditResetDate}
                  onSave={(date: moment.Moment | null) => {
                    this.handleDateChange(date, "actual");
                  }}
                />
              </div>
              {isMobile && (
                <div className="date-label-group" data-qa="actual-reset-date">
                  <h6>Actual Reset Time</h6>
                  <DatePickerPopover
                    field={true}
                    fieldValue={this.props.store2project.actual_reset_date}
                    defaultValue={""}
                    isMobile={isMobile}
                    disabled={!canEditResetDate}
                    timeOnly={true}
                    onSave={(date: moment.Moment | null) => {
                      this.handleTimeChange(date, "actual");
                    }}
                  />
                </div>
              )}

              <div className="implementation-dropdown">
                <h6 style={{ marginBottom: "5px" }}>Implementation</h6>
                <Dropdown
                  isDisabled={!canEditImplementation}
                  dataQa="implementation-dropdown"
                  noShadow={true}
                  backgroundFill={true}
                  options={implementationOptions}
                  value={{ label: this.state.implementation }}
                  placeholder={"Implementation"}
                  onChange={(value) => {
                    const selected = value.value; 
                    this.setState({ 
                      implementation: selected,
                      userSelectedImplementation: true,
                    }, () => {
                      // callback ensures state is set before any update cycle
                      this.props.onImplementationChange(selected);
                    });
                  }}
                  isSearchable
                />
              </div>
            </div>
          </Card.Body>

          <Card.Body className="note-form">
            <div className="note-form-input-group">
              <textarea
                className="note-text"
                id="add_resetnote"
                data-qa="note-text"
                placeholder="Type note here"
                onChange={this.handleTextChange}
                value={resetNote}
              />
            </div>
            <div hidden={!this.props.displaySurveys}>
              <button
                style={{
                  // backgroundColor: "#E5B611",
                  border: "1px solid #E5B611",
                  color: "#E5B611",
                  outline: "none",
                  fontSize: "14px",
                  borderRadius: "4px",
                  padding: "5px 10px",
                  // border: "none",
                  float: "left",
                }}
                // secondary={true}
                onClick={this.handleTakeSurveys}
                data-qa="take-surveys-button"
              >
                Take Survey
              </button>
            </div>
            <form onSubmit={this.handleSubmit}>
              <div className="submit-group">
                <span
                  data-qa="note-char-count"
                  className={cx("comment-char-count", {
                    warn: this.state.noteOverLength,
                  })}
                >
                  {this.state.resetNote.length} / {this.MAX_NOTE_LENGTH} characters
                </span>
                <button
                  style={{
                    backgroundColor: "#E5B611",
                    outline: "none",
                    fontSize: "14px",
                    borderRadius: "4px",
                    padding: "5px 10px",
                    border: "none",
                  }}
                  type="submit"
                  data-qa="add-resetnote-button"
                  disabled={this.state.noteOverLength || submitDisabled}
                >
                  Add Note
                </button>
              </div>
            </form>
          </Card.Body>
        </div>

{this.props.resetnotes?.length > 0 && (
<div
          style={{
            borderRadius: "16px",
            border: "1px solid #e3e6ea",
            overflow: "hidden",
            marginTop: "10px",
          }}
        >
          <Table
            isMobile={isMobile}
            data={this.props.resetnotes || []}
            columns={[
              {
                field: "implementation",
                header: "Implementation",
                dataQa: "implementation",
              },
              {
                field: "resetnote",
                header: "Reset Note",
                dataQa: "reset_note",
                minWidth: 300,
              },
              {
                field: "created_at",
                header: "Note Date",
                formatter: (value) => {
                  return moment(value, Constants.DATE_FORMAT__YYYY_MM_DD).format("M/D/YYYY");
                },
                dataQa: "note_date",
              },
            ]}
            emptyState="No notes found"
          />
        </div>
)}
      </div>
    );
  }
}

export default ResetNotesUI;
